import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
} from '@mui/material';
import {
  type PenaltyBalanceOption,
  type PenaltyReason,
  type PenaltyTraderOption,
  tradersService,
} from '@/services/traders/traders.service';

const PENALTY_REASONS: Array<{ value: PenaltyReason; label: string }> = [
  { value: 'one_time_sla_violation', label: 'Однократное нарушение SLA' },
  { value: 'no_response_to_appeal', label: 'Нет ответа по апелляции' },
  { value: 'late_supporting_evidence', label: 'Доказательства предоставлены с опозданием' },
  { value: 'repeated_sla_violations', label: 'Повторные нарушения SLA' },
  { value: 'outdated_payment_details', label: 'Активны неактуальные реквизиты' },
  { value: 'communication_rules_breach', label: 'Нарушение правил коммуникации' },
  { value: 'security_deposit_below_minimum', label: 'Страховой депозит ниже минимума' },
  { value: 'confirmed_fraud', label: 'Подтверждённый фрод' },
  { value: 'other', label: 'Другое' },
];

const traderLabel = (trader: PenaltyTraderOption) => trader.name || 'Без имени';

const balanceLabel = (balance: PenaltyBalanceOption) =>
  `${balance.currency_iso_code || 'Без валюты'} · доступно: ${balance.available_balance}`;

type PenaltyHistoryItem = { id: string; created_at: string; trader_id: string; trader_name: string | null; currency: string | null; amount: string; description: string | null; reason: PenaltyReason | null; admin_name: string | null; admin_id: string | null; transaction_id: string | null };

export default function Penalties() {
  const [traders, setTraders] = useState<PenaltyTraderOption[]>([]);
  const [selectedTrader, setSelectedTrader] = useState<PenaltyTraderOption | null>(null);
  const [balances, setBalances] = useState<PenaltyBalanceOption[]>([]);
  const [selectedBalance, setSelectedBalance] = useState<PenaltyBalanceOption | null>(null);
  const [reason, setReason] = useState<PenaltyReason>('one_time_sla_violation');
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [history, setHistory] = useState<PenaltyHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  const [historyRevision, setHistoryRevision] = useState(0);
  const [historyError, setHistoryError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setHistoryError('');
    tradersService.getPenaltyHistory({ trader_id: selectedTrader?.id, from_date: historyFrom || undefined, to_date: historyTo || undefined, limit: 25, offset: historyPage * 25 })
      .then((result) => { if (!cancelled) { setHistory(result.items); setHistoryTotal(result.total); } })
      .catch(() => { if (!cancelled) setHistoryError('Не удалось загрузить историю штрафов'); });
    return () => { cancelled = true; };
  }, [selectedTrader?.id, historyFrom, historyTo, historyPage, historyRevision]);
  const [loadingTraders, setLoadingTraders] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [penaltyId, setPenaltyId] = useState(() => crypto.randomUUID());

  const amountIsValid = useMemo(() => {
    const parsed = Number(amount);
    return Number.isFinite(parsed) && parsed > 0;
  }, [amount]);

  const loadTraders = async (search = '') => {
    setLoadingTraders(true);
    try {
      const response = await tradersService.searchPenaltyTraders(search);
      setTraders(response.items);
    } catch {
      setError('Не удалось загрузить список трейдеров.');
    } finally {
      setLoadingTraders(false);
    }
  };

  useEffect(() => {
    void loadTraders();
  }, []);

  useEffect(() => {
    if (!selectedTrader) {
      setBalances([]);
      setSelectedBalance(null);
      return;
    }

    let cancelled = false;
    setLoadingBalances(true);
    setSelectedBalance(null);
    void tradersService.getPenaltyTraderBalances(selectedTrader.id)
      .then((response) => {
        if (!cancelled) setBalances(response);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить балансы трейдера.');
      })
      .finally(() => {
        if (!cancelled) setLoadingBalances(false);
      });

    return () => { cancelled = true; };
  }, [selectedTrader]);

  const submit = async () => {
    if (!selectedTrader || !selectedBalance || !amountIsValid) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await tradersService.createTraderPenalty({
        penalty_id: penaltyId,
        trader_id: selectedTrader.id,
        balance_id: selectedBalance.id,
        amount,
        reason,
        comment: comment.trim() || undefined,
        transaction_id: transactionId.trim() || undefined,
      });
      setSuccess(`Штраф сохранён. Доступный баланс: ${result.new_balance}`);
      setAmount('');
      setComment('');
      setTransactionId('');
      setHistoryRevision((value) => value + 1);
      setPenaltyId(crypto.randomUUID());
      const updatedBalances = await tradersService.getPenaltyTraderBalances(selectedTrader.id);
      setBalances(updatedBalances);
      setSelectedBalance(updatedBalances.find((item) => item.id === selectedBalance.id) || null);
    } catch {
      setError('Не удалось сохранить штраф. Повторная отправка безопасна и не создаст второе списание.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', py: 3 }}>
      <Typography variant="h4" mb={0.5}>Штрафы трейдеров</Typography>
      <Typography color="text.secondary" mb={3}>
        Списание выполняется только с выбранного доступного баланса. Отрицательный баланс допустим.
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>}
          <Autocomplete
            options={traders}
            value={selectedTrader}
            loading={loadingTraders}
            getOptionLabel={traderLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => { setSelectedTrader(value); setHistoryPage(0); }}
            onInputChange={(_, value, changeReason) => {
              if (changeReason === 'input') void loadTraders(value);
            }}
            renderInput={(params) => <TextField {...params} label="Трейдер" required InputProps={{ ...params.InputProps, endAdornment: <>{loadingTraders ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</> }} />}
          />
          <Autocomplete
            options={balances}
            value={selectedBalance}
            loading={loadingBalances}
            disabled={!selectedTrader || loadingBalances}
            getOptionLabel={balanceLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setSelectedBalance(value)}
            renderInput={(params) => <TextField {...params} label="Баланс для списания" required InputProps={{ ...params.InputProps, endAdornment: <>{loadingBalances ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</> }} />}
          />
          <TextField select required label="Причина штрафа" value={reason} onChange={(event) => setReason(event.target.value as PenaltyReason)}>
            {PENALTY_REASONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </TextField>
          <TextField required label="Сумма штрафа" value={amount} onChange={(event) => setAmount(event.target.value)} inputProps={{ inputMode: 'decimal' }} error={Boolean(amount) && !amountIsValid} helperText={Boolean(amount) && !amountIsValid ? 'Введите сумму больше нуля.' : 'Будет списана с available_balance.'} />
          <TextField label="Комментарий" value={comment} onChange={(event) => setComment(event.target.value)} multiline minRows={3} inputProps={{ maxLength: 1000 }} />
          <TextField label="ID связанной транзакции" value={transactionId} onChange={(event) => setTransactionId(event.target.value)} helperText="Необязательно. Транзакция должна принадлежать выбранному трейдеру. Причина и описание сохраняются в журнале." />
          <Box>
            <Button variant="contained" disabled={saving || !selectedTrader || !selectedBalance || !amountIsValid} onClick={() => void submit()}>
              {saving ? 'Сохраняем…' : 'Сохранить штраф'}
            </Button>
          </Box>
        </Stack>
      </Paper>
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6">История штрафов{selectedTrader ? ` · ${traderLabel(selectedTrader)}` : ' · все трейдеры'}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} my={2}>
          <TextField type="date" label="С (UTC)" value={historyFrom} onChange={(event) => { setHistoryFrom(event.target.value); setHistoryPage(0); }} InputLabelProps={{ shrink: true }} />
          <TextField type="date" label="По (UTC)" value={historyTo} onChange={(event) => { setHistoryTo(event.target.value); setHistoryPage(0); }} InputLabelProps={{ shrink: true }} />
        </Stack>
        {historyError && <Alert severity="error">{historyError}</Alert>}
        <TableContainer><Table size="small"><TableHead><TableRow>
          {['Дата', 'Трейдер', 'Сумма', 'Назначил', 'Причина и описание', 'Транзакция'].map((label) => <TableCell key={label}>{label}</TableCell>)}
        </TableRow></TableHead><TableBody>
          {history.map((item) => <TableRow key={item.id}>
            <TableCell>{new Date(item.created_at).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })}</TableCell>
            <TableCell>{item.trader_name || item.trader_id}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.amount} {item.currency || 'Валюта неизвестна'}</TableCell>
            <TableCell>{item.admin_name || item.admin_id || 'Неизвестно'}</TableCell>
            <TableCell><Typography variant="body2">{PENALTY_REASONS.find((reason) => reason.value === item.reason)?.label || 'Причина не распознана'}</Typography><Typography variant="caption" color="text.secondary">{item.description || 'Не указано'}</Typography></TableCell>
            <TableCell>{item.transaction_id || 'Не привязана'}</TableCell>
          </TableRow>)}
          {!history.length && !historyError && <TableRow><TableCell colSpan={6}>Штрафов нет</TableCell></TableRow>}
        </TableBody></Table></TableContainer>
        <TablePagination component="div" count={historyTotal} page={historyPage} rowsPerPage={25} rowsPerPageOptions={[25]} onPageChange={(_, page) => setHistoryPage(page)} />
      </Paper>
    </Box>
  );
}
